// LocalDatabase.ts
// Handles the database emulation in localStorage, seeding it with initial data,
// and providing repository instances that emulate an asynchronous database access.
import { normalizeString } from '../utils/stringUtils';

export interface DbBrand {
  id: string;
  name: string;
  country?: string;
  created_at: string;
  updated_at: string;
}

export interface DbSeries {
  id: string;
  name: string;
  description?: string;
}

export interface DbFlavor {
  id: string;
  brand_id: string;
  name: string;
  series_id?: string;
  description?: string;
  nicotine?: boolean;
  created_at: string;
  updated_at: string;
}

export interface DbFlavorTasteVectorMaster {
  flavor_id: string;
  sweetness: number;
  sourness: number;
  coolness: number;
  richness: number;
  fruitiness: number;
  aroma: number;
  aftertaste: number;
}

export interface DbUserFlavor {
  id: string;
  user_id: string;
  flavor_id: string;
  owned: boolean;
  rating?: number;
  memo?: string;
  created_at: string;
  updated_at: string;
}

export interface DbUserFlavorTasteVector {
  user_flavor_id: string;
  sweetness: number;
  sourness: number;
  coolness: number;
  richness: number;
  fruitiness: number;
  aroma: number;
  aftertaste: number;
}

export interface DbMix {
  id: string;
  user_id: string;
  name: string;
  impression?: string;
  rating?: number;
  is_public: boolean;
  is_favorite?: boolean;
  created_at: string;
  updated_at: string;
}

export interface DbMixFlavor {
  id: string;
  mix_id: string;
  flavor_id: string;
  custom_flavor_name?: string;
  ratio: number;
  sort_order: number;
}

// ULID Generator (Lexicographically sortable unique IDs)
export function generateULID(): string {
  const CHARS = '0123456789ABCDEFGHJKMNPQRSTVWXYZ';
  let time = Date.now();
  let timeStr = '';
  for (let i = 9; i >= 0; i--) {
    timeStr = CHARS[time % 32] + timeStr;
    time = Math.floor(time / 32);
  }
  let randStr = '';
  for (let i = 0; i < 16; i++) {
    randStr += CHARS[Math.floor(Math.random() * 32)];
  }
  return timeStr + randStr;
}

// Database Keys
const KEYS = {
  BRANDS: 'hf_brands',
  SERIES: 'hf_series',
  FLAVORS: 'hf_flavors',
  FLAVOR_TASTE_MASTER: 'hf_flavor_taste_master',
  USER_FLAVORS: 'hf_user_flavors',
  USER_FLAVOR_TASTE: 'hf_user_flavor_taste',
  MIXES: 'hf_mixes',
  MIX_FLAVORS: 'hf_mix_flavors',
};

// Seed Data
const DEFAULT_BRANDS: DbBrand[] = [
  {
    id: 'N0001',
    name: 'Al Fakher',
    country: 'UAE',
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  },
  {
    id: 'N0002',
    name: 'Fumari',
    country: 'USA',
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  },
  {
    id: 'N0003',
    name: 'Social Smoke',
    country: 'USA',
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  },
  {
    id: 'N0004',
    name: 'Tangiers',
    country: 'USA',
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  },
  {
    id: 'N0005',
    name: 'Eternal Smoke',
    country: 'USA',
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  },
  {
    id: 'N0006',
    name: 'Azure',
    country: 'USA',
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  },
  {
    id: 'N0007',
    name: 'Trifecta',
    country: 'USA',
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  },
  {
    id: 'N0008',
    name: 'Afzal',
    country: 'India',
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  },
  {
    id: 'N0009',
    name: 'Nakhla',
    country: 'Egypt',
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  },
  {
    id: 'N0010',
    name: 'Decloud',
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  },
  {
    id: 'N0011',
    name: 'Dozaji',
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  },
  {
    id: 'N0012',
    name: 'Debaji',
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  },
];

const DEFAULT_SERIES: DbSeries[] = [
  { id: 's-fruit', name: 'フルーツ（甘い）' },
  { id: 's-fruit-sour', name: 'フルーツ（さっぱり）' },
  { id: 's-dessert', name: 'お菓子' },
  { id: 's-cafe', name: 'ドリンク系' },
  { id: 's-spice', name: 'スパイス' },
  { id: 's-mint', name: 'ハーブ' },
  { id: 's-nut', name: 'ナッツ' },
  { id: 's-other', name: 'その他' },
];

// Map popular flavors to brands & series
const POPULAR_FLAVORS_MAPPING = [
  {
    brandId: 'N0001',
    seriesId: 's-fruit',
    name: 'Two Apples',
    desc: 'アニスの効いた伝統的なダブルアップル',
    sweetness: 4,
    sourness: 1,
    coolness: 1,
    richness: 5,
    fruitiness: 4,
    aroma: 4,
    aftertaste: 4,
  },
  {
    brandId: 'N0001',
    seriesId: 's-mint',
    name: 'Mint',
    desc: 'シンプルで定番のクリーンミント',
    sweetness: 1,
    sourness: 1,
    coolness: 4,
    richness: 3,
    fruitiness: 1,
    aroma: 3,
    aftertaste: 3,
  },
  {
    brandId: 'N0001',
    seriesId: 's-fruit',
    name: 'Lemon',
    desc: '爽やかな酸味のあるプレーンレモン',
    sweetness: 2,
    sourness: 4,
    coolness: 1,
    richness: 3,
    fruitiness: 4,
    aroma: 3,
    aftertaste: 3,
  },
  {
    brandId: 'N0001',
    seriesId: 's-other',
    name: 'Rose',
    desc: '華やかで気品のある上品なバラ香',
    sweetness: 2,
    sourness: 1,
    coolness: 1,
    richness: 4,
    fruitiness: 1,
    aroma: 5,
    aftertaste: 4,
  },
  {
    brandId: 'N0002',
    seriesId: 's-spice',
    name: 'Cinnamon',
    desc: '温かみのある本格的なシナモン',
    sweetness: 3,
    sourness: 1,
    coolness: 1,
    richness: 4,
    fruitiness: 1,
    aroma: 4,
    aftertaste: 4,
  },
  {
    brandId: 'N0002',
    seriesId: 's-dessert',
    name: 'Blueberry Muffin',
    desc: 'ブルーベリーと焼き立てマフィンの甘い香り',
    sweetness: 5,
    sourness: 2,
    coolness: 1,
    richness: 5,
    fruitiness: 4,
    aroma: 5,
    aftertaste: 4,
  },
  {
    brandId: 'N0002',
    seriesId: 's-dessert',
    name: 'French Vanilla',
    desc: 'クリーミーで濃厚な極上バニラ',
    sweetness: 5,
    sourness: 1,
    coolness: 1,
    richness: 5,
    fruitiness: 1,
    aroma: 4,
    aftertaste: 5,
  },
  {
    brandId: 'N0002',
    seriesId: 's-cafe',
    name: 'Spiced Chai',
    desc: '甘くスパイシーなチャイの味わい',
    sweetness: 4,
    sourness: 1,
    coolness: 1,
    richness: 4,
    fruitiness: 1,
    aroma: 4,
    aftertaste: 4,
  },
  {
    brandId: 'N0003',
    seriesId: 's-cafe',
    name: 'Chai Latte',
    desc: 'ミルク感の強い甘めのチャイラテ',
    sweetness: 5,
    sourness: 1,
    coolness: 1,
    richness: 4,
    fruitiness: 1,
    aroma: 4,
    aftertaste: 4,
  },
  {
    brandId: 'N0003',
    seriesId: 's-fruit',
    name: 'Pear Chill',
    desc: 'ひんやりした洋梨の瑞々しい甘さ',
    sweetness: 4,
    sourness: 2,
    coolness: 3,
    richness: 4,
    fruitiness: 5,
    aroma: 4,
    aftertaste: 4,
  },
  {
    brandId: 'N0003',
    seriesId: 's-fruit',
    name: 'Lime',
    desc: 'すっきりとした苦味と酸味のあるライム',
    sweetness: 2,
    sourness: 4,
    coolness: 1,
    richness: 3,
    fruitiness: 4,
    aroma: 3,
    aftertaste: 3,
  },
  {
    brandId: 'N0004',
    seriesId: 's-mint',
    name: 'Cane Mint',
    desc: 'タンジールを代表する強烈なペパーミント',
    sweetness: 1,
    sourness: 1,
    coolness: 5,
    richness: 4,
    fruitiness: 1,
    aroma: 4,
    aftertaste: 4,
  },
  {
    brandId: 'N0008',
    seriesId: 's-cafe',
    name: 'Earl Grey',
    desc: 'ベルガモットが上品に香る本格アールグレイ紅茶',
    sweetness: 3,
    sourness: 2,
    coolness: 1,
    richness: 4,
    fruitiness: 2,
    aroma: 5,
    aftertaste: 4,
  },
  {
    brandId: 'N0005',
    seriesId: 's-fruit',
    name: 'Red Lips',
    desc: 'ストロベリーをベースにした甘酸っぱいミックスフレーバー',
    sweetness: 4,
    sourness: 3,
    coolness: 1,
    richness: 4,
    fruitiness: 5,
    aroma: 4,
    aftertaste: 4,
  },
  {
    brandId: 'N0005',
    seriesId: 's-fruit',
    name: 'Watermelon Lit',
    desc: '瑞々しく甘いスイカフレーバー',
    sweetness: 4,
    sourness: 1,
    coolness: 2,
    richness: 4,
    fruitiness: 4,
    aroma: 3,
    aftertaste: 3,
  },
  {
    brandId: 'N0009',
    seriesId: 's-spice',
    name: 'Cardamom',
    desc: '独特の清涼感とスパイシーさを持つカルダモン',
    sweetness: 2,
    sourness: 1,
    coolness: 2,
    richness: 4,
    fruitiness: 1,
    aroma: 5,
    aftertaste: 4,
  },
  { brandId: 'N0010', seriesId: 's-fruit', name: 'Green Apple', desc: 'Decloud Green Apple', sweetness: 3, sourness: 3, coolness: 1, richness: 3, fruitiness: 4, aroma: 3, aftertaste: 3, nicotine: false },
  { brandId: 'N0010', seriesId: 's-fruit', name: 'Guava', desc: 'Decloud Guava', sweetness: 4, sourness: 2, coolness: 1, richness: 3, fruitiness: 4, aroma: 3, aftertaste: 3, nicotine: false },
  { brandId: 'N0010', seriesId: 's-dessert', name: 'Honey', desc: 'Decloud Honey', sweetness: 5, sourness: 1, coolness: 1, richness: 4, fruitiness: 1, aroma: 4, aftertaste: 4, nicotine: false },
  { brandId: 'N0010', seriesId: 's-mint', name: 'Ice', desc: 'Decloud Ice', sweetness: 1, sourness: 1, coolness: 5, richness: 2, fruitiness: 1, aroma: 2, aftertaste: 2, nicotine: false },
  { brandId: 'N0010', seriesId: 's-other', name: 'Jasmine', desc: 'Decloud Jasmine', sweetness: 2, sourness: 1, coolness: 1, richness: 3, fruitiness: 1, aroma: 5, aftertaste: 4, nicotine: false },
  { brandId: 'N0010', seriesId: 's-fruit', name: 'Kiwi', desc: 'Decloud Kiwi', sweetness: 3, sourness: 3, coolness: 1, richness: 3, fruitiness: 4, aroma: 3, aftertaste: 3, nicotine: false },
  { brandId: 'N0010', seriesId: 's-fruit-sour', name: 'Lemon', desc: 'Decloud Lemon', sweetness: 2, sourness: 4, coolness: 1, richness: 3, fruitiness: 4, aroma: 4, aftertaste: 3, nicotine: false },
  { brandId: 'N0010', seriesId: 's-mint', name: 'Lemongrass', desc: 'Decloud Lemongrass', sweetness: 2, sourness: 2, coolness: 2, richness: 3, fruitiness: 2, aroma: 4, aftertaste: 3, nicotine: false },
  { brandId: 'N0010', seriesId: 's-fruit', name: 'Lychee', desc: 'Decloud Lychee', sweetness: 4, sourness: 2, coolness: 1, richness: 3, fruitiness: 4, aroma: 4, aftertaste: 3, nicotine: false },
  { brandId: 'N0010', seriesId: 's-fruit', name: 'Melon', desc: 'Decloud Melon', sweetness: 4, sourness: 1, coolness: 1, richness: 3, fruitiness: 4, aroma: 4, aftertaste: 3, nicotine: false },
  { brandId: 'N0010', seriesId: 's-cafe', name: 'Milk Tea', desc: 'Decloud Milk Tea', sweetness: 4, sourness: 1, coolness: 1, richness: 4, fruitiness: 1, aroma: 4, aftertaste: 4, nicotine: false },
  { brandId: 'N0010', seriesId: 's-mint', name: 'Mint', desc: 'Decloud Mint', sweetness: 2, sourness: 1, coolness: 4, richness: 3, fruitiness: 1, aroma: 4, aftertaste: 3, nicotine: false },
  { brandId: 'N0010', seriesId: 's-fruit', name: 'Orange', desc: 'Decloud Orange', sweetness: 3, sourness: 3, coolness: 1, richness: 3, fruitiness: 4, aroma: 4, aftertaste: 3, nicotine: false },
  { brandId: 'N0010', seriesId: 's-fruit', name: 'Peach', desc: 'Decloud Peach', sweetness: 4, sourness: 2, coolness: 1, richness: 3, fruitiness: 4, aroma: 4, aftertaste: 3, nicotine: false },
  { brandId: 'N0010', seriesId: 's-fruit', name: 'Plum', desc: 'Decloud Plum', sweetness: 3, sourness: 3, coolness: 1, richness: 3, fruitiness: 4, aroma: 4, aftertaste: 3, nicotine: false },
  { brandId: 'N0010', seriesId: 's-fruit', name: 'Pomegranate Passion', desc: 'Decloud Pomegranate Passion', sweetness: 3, sourness: 3, coolness: 1, richness: 3, fruitiness: 4, aroma: 4, aftertaste: 3, nicotine: false },
  { brandId: 'N0010', seriesId: 's-other', name: 'Sakura', desc: 'Decloud Sakura', sweetness: 3, sourness: 2, coolness: 1, richness: 3, fruitiness: 2, aroma: 5, aftertaste: 4, nicotine: false },
  { brandId: 'N0010', seriesId: 's-fruit-sour', name: 'Salty Lemon', desc: 'Decloud Salty Lemon', sweetness: 2, sourness: 4, coolness: 1, richness: 3, fruitiness: 4, aroma: 4, aftertaste: 3, nicotine: false },
  { brandId: 'N0010', seriesId: 's-fruit-sour', name: 'Sour Apple & Kiwi', desc: 'Decloud Sour Apple & Kiwi', sweetness: 2, sourness: 4, coolness: 1, richness: 3, fruitiness: 4, aroma: 4, aftertaste: 3, nicotine: false },
  { brandId: 'N0010', seriesId: 's-dessert', name: 'Sweet Potato', desc: 'Decloud Sweet Potato', sweetness: 4, sourness: 1, coolness: 1, richness: 4, fruitiness: 1, aroma: 4, aftertaste: 4, nicotine: false },
  { brandId: 'N0010', seriesId: 's-dessert', name: 'Vanilla', desc: 'Decloud Vanilla', sweetness: 4, sourness: 1, coolness: 1, richness: 4, fruitiness: 1, aroma: 4, aftertaste: 4, nicotine: false },
  { brandId: 'N0010', seriesId: 's-fruit', name: 'White Grape', desc: 'Decloud White Grape', sweetness: 4, sourness: 2, coolness: 1, richness: 3, fruitiness: 4, aroma: 4, aftertaste: 3, nicotine: false },
  { brandId: 'N0010', seriesId: 's-dessert', name: 'Yogurt', desc: 'Decloud Yogurt', sweetness: 3, sourness: 3, coolness: 1, richness: 4, fruitiness: 2, aroma: 3, aftertaste: 3, nicotine: false },
  { brandId: 'N0010', seriesId: 's-fruit-sour', name: 'Yuzu', desc: 'Decloud Yuzu', sweetness: 2, sourness: 4, coolness: 1, richness: 3, fruitiness: 4, aroma: 4, aftertaste: 3, nicotine: false },
  { brandId: 'N0010', seriesId: 's-dessert', name: 'Azuki', desc: 'Decloud Azuki', sweetness: 4, sourness: 1, coolness: 1, richness: 4, fruitiness: 1, aroma: 3, aftertaste: 4, nicotine: false },
  { brandId: 'N0010', seriesId: 's-dessert', name: 'Coconut', desc: 'Decloud Coconut', sweetness: 4, sourness: 1, coolness: 1, richness: 4, fruitiness: 2, aroma: 4, aftertaste: 3, nicotine: false },
  { brandId: 'N0010', seriesId: 's-cafe', name: 'Cola', desc: 'Decloud Cola', sweetness: 4, sourness: 2, coolness: 2, richness: 3, fruitiness: 1, aroma: 4, aftertaste: 3, nicotine: false },
  { brandId: 'N0010', seriesId: 's-cafe', name: 'Ginger Ale', desc: 'Decloud Ginger Ale', sweetness: 3, sourness: 2, coolness: 2, richness: 3, fruitiness: 1, aroma: 4, aftertaste: 3, nicotine: false },
  { brandId: 'N0010', seriesId: 's-cafe', name: 'Matcha', desc: 'Decloud Matcha', sweetness: 3, sourness: 1, coolness: 1, richness: 4, fruitiness: 1, aroma: 4, aftertaste: 4, nicotine: false },
  // -- Al Fakher --
  { brandId: 'N0001', seriesId: 's-fruit', name: 'Grape', desc: 'Al Fakher Grape', sweetness: 4, sourness: 1, coolness: 1, richness: 3, fruitiness: 4, aroma: 3, aftertaste: 3 },
  { brandId: 'N0001', seriesId: 's-fruit', name: 'Grape with Mint', desc: 'Al Fakher Grape with Mint', sweetness: 3, sourness: 1, coolness: 3, richness: 3, fruitiness: 4, aroma: 3, aftertaste: 3 },
  { brandId: 'N0001', seriesId: 's-fruit', name: 'Blueberry', desc: 'Al Fakher Blueberry', sweetness: 4, sourness: 2, coolness: 1, richness: 3, fruitiness: 4, aroma: 3, aftertaste: 3 },
  { brandId: 'N0001', seriesId: 's-fruit', name: 'Blueberry with Mint', desc: 'Al Fakher Blueberry with Mint', sweetness: 3, sourness: 2, coolness: 3, richness: 3, fruitiness: 4, aroma: 3, aftertaste: 3 },
  { brandId: 'N0001', seriesId: 's-fruit', name: 'Watermelon', desc: 'Al Fakher Watermelon', sweetness: 4, sourness: 1, coolness: 1, richness: 3, fruitiness: 4, aroma: 3, aftertaste: 3 },
  { brandId: 'N0001', seriesId: 's-fruit', name: 'Watermelon with Mint', desc: 'Al Fakher Watermelon with Mint', sweetness: 3, sourness: 1, coolness: 3, richness: 3, fruitiness: 4, aroma: 3, aftertaste: 3 },
  { brandId: 'N0001', seriesId: 's-fruit', name: 'Orange', desc: 'Al Fakher Orange', sweetness: 3, sourness: 3, coolness: 1, richness: 3, fruitiness: 4, aroma: 3, aftertaste: 3 },
  { brandId: 'N0001', seriesId: 's-fruit', name: 'Orange with Mint', desc: 'Al Fakher Orange with Mint', sweetness: 2, sourness: 3, coolness: 3, richness: 3, fruitiness: 4, aroma: 3, aftertaste: 3 },
  { brandId: 'N0001', seriesId: 's-fruit-sour', name: 'Lemon with Mint', desc: 'Al Fakher Lemon with Mint', sweetness: 2, sourness: 4, coolness: 3, richness: 3, fruitiness: 4, aroma: 3, aftertaste: 3 },
  { brandId: 'N0001', seriesId: 's-fruit', name: 'Peach', desc: 'Al Fakher Peach', sweetness: 4, sourness: 1, coolness: 1, richness: 3, fruitiness: 4, aroma: 3, aftertaste: 3 },
  { brandId: 'N0001', seriesId: 's-fruit', name: 'Cherry', desc: 'Al Fakher Cherry', sweetness: 4, sourness: 2, coolness: 1, richness: 3, fruitiness: 4, aroma: 4, aftertaste: 3 },
  { brandId: 'N0001', seriesId: 's-fruit', name: 'Strawberry', desc: 'Al Fakher Strawberry', sweetness: 4, sourness: 2, coolness: 1, richness: 3, fruitiness: 4, aroma: 3, aftertaste: 3 },
  { brandId: 'N0001', seriesId: 's-fruit', name: 'Mango', desc: 'Al Fakher Mango', sweetness: 4, sourness: 2, coolness: 1, richness: 3, fruitiness: 5, aroma: 4, aftertaste: 3 },
  { brandId: 'N0001', seriesId: 's-fruit', name: 'Melon', desc: 'Al Fakher Melon', sweetness: 4, sourness: 1, coolness: 1, richness: 3, fruitiness: 4, aroma: 3, aftertaste: 3 },
  { brandId: 'N0001', seriesId: 's-other', name: 'Gum with Mint', desc: 'Al Fakher Gum with Mint', sweetness: 3, sourness: 1, coolness: 4, richness: 3, fruitiness: 1, aroma: 3, aftertaste: 3 },
  { brandId: 'N0001', seriesId: 's-other', name: 'Magic Love', desc: 'Al Fakher Magic Love', sweetness: 4, sourness: 2, coolness: 2, richness: 3, fruitiness: 3, aroma: 4, aftertaste: 3 },
  { brandId: 'N0001', seriesId: 's-fruit-sour', name: 'Kiwi', desc: 'Al Fakher Kiwi', sweetness: 3, sourness: 3, coolness: 1, richness: 3, fruitiness: 4, aroma: 3, aftertaste: 3 },
  // -- Fumari --
  { brandId: 'N0002', seriesId: 's-dessert', name: 'White Gummi Bear', desc: 'Fumari White Gummi Bear', sweetness: 5, sourness: 1, coolness: 1, richness: 4, fruitiness: 3, aroma: 4, aftertaste: 4 },
  { brandId: 'N0002', seriesId: 's-fruit', name: 'Ambrosia', desc: 'Fumari Ambrosia', sweetness: 4, sourness: 2, coolness: 1, richness: 3, fruitiness: 4, aroma: 4, aftertaste: 3 },
  { brandId: 'N0002', seriesId: 's-dessert', name: 'Red Gummi Bear', desc: 'Fumari Red Gummi Bear', sweetness: 5, sourness: 2, coolness: 1, richness: 4, fruitiness: 3, aroma: 4, aftertaste: 4 },
  { brandId: 'N0002', seriesId: 's-dessert', name: 'Mint Chocolate Chill', desc: 'Fumari Mint Chocolate Chill', sweetness: 4, sourness: 1, coolness: 3, richness: 5, fruitiness: 1, aroma: 4, aftertaste: 4 },
  { brandId: 'N0002', seriesId: 's-fruit', name: 'Tropical Punch', desc: 'Fumari Tropical Punch', sweetness: 4, sourness: 2, coolness: 1, richness: 3, fruitiness: 5, aroma: 4, aftertaste: 3 },
  { brandId: 'N0002', seriesId: 's-fruit-sour', name: 'Lemon Mint', desc: 'Fumari Lemon Mint', sweetness: 2, sourness: 4, coolness: 3, richness: 3, fruitiness: 4, aroma: 3, aftertaste: 3 },
  { brandId: 'N0002', seriesId: 's-fruit', name: 'Mandarin Zest', desc: 'Fumari Mandarin Zest', sweetness: 3, sourness: 3, coolness: 1, richness: 3, fruitiness: 4, aroma: 4, aftertaste: 3 },
  { brandId: 'N0002', seriesId: 's-fruit', name: 'Prickly Pear', desc: 'Fumari Prickly Pear', sweetness: 3, sourness: 2, coolness: 1, richness: 3, fruitiness: 4, aroma: 3, aftertaste: 3 },
  { brandId: 'N0002', seriesId: 's-fruit', name: 'Island Papaya', desc: 'Fumari Island Papaya', sweetness: 4, sourness: 2, coolness: 1, richness: 3, fruitiness: 5, aroma: 4, aftertaste: 3 },
  { brandId: 'N0002', seriesId: 's-dessert', name: 'Orange Cream', desc: 'Fumari Orange Cream', sweetness: 4, sourness: 2, coolness: 1, richness: 4, fruitiness: 3, aroma: 4, aftertaste: 4 },
  { brandId: 'N0002', seriesId: 's-fruit-sour', name: 'Sour Cherry', desc: 'Fumari Sour Cherry', sweetness: 3, sourness: 3, coolness: 1, richness: 3, fruitiness: 4, aroma: 4, aftertaste: 3 },
  { brandId: 'N0002', seriesId: 's-fruit-sour', name: 'Raspberry', desc: 'Fumari Raspberry', sweetness: 3, sourness: 3, coolness: 1, richness: 3, fruitiness: 4, aroma: 3, aftertaste: 3 },
  { brandId: 'N0002', seriesId: 's-fruit', name: 'Watermelon', desc: 'Fumari Watermelon', sweetness: 4, sourness: 1, coolness: 1, richness: 3, fruitiness: 4, aroma: 3, aftertaste: 3 },
  { brandId: 'N0002', seriesId: 's-fruit', name: 'Nectarine', desc: 'Fumari Nectarine', sweetness: 4, sourness: 2, coolness: 1, richness: 3, fruitiness: 4, aroma: 4, aftertaste: 3 },
  { brandId: 'N0002', seriesId: 's-fruit', name: 'White Peach', desc: 'Fumari White Peach', sweetness: 4, sourness: 1, coolness: 1, richness: 3, fruitiness: 4, aroma: 4, aftertaste: 3 },
  { brandId: 'N0002', seriesId: 's-fruit', name: 'Blueberry', desc: 'Fumari Blueberry', sweetness: 4, sourness: 2, coolness: 1, richness: 3, fruitiness: 4, aroma: 3, aftertaste: 3 },
  { brandId: 'N0002', seriesId: 's-mint', name: 'Mint', desc: 'Fumari Mint', sweetness: 1, sourness: 1, coolness: 4, richness: 3, fruitiness: 1, aroma: 3, aftertaste: 3 },
  // -- Social Smoke --
  { brandId: 'N0003', seriesId: 's-other', name: 'Voltage', desc: 'Social Smoke Voltage', sweetness: 3, sourness: 2, coolness: 3, richness: 3, fruitiness: 3, aroma: 3, aftertaste: 3 },
  { brandId: 'N0003', seriesId: 's-fruit-sour', name: 'Pink Lemonade', desc: 'Social Smoke Pink Lemonade', sweetness: 3, sourness: 3, coolness: 1, richness: 3, fruitiness: 4, aroma: 3, aftertaste: 3 },
  { brandId: 'N0003', seriesId: 's-mint', name: 'Absolute Zero', desc: 'Social Smoke Absolute Zero', sweetness: 1, sourness: 1, coolness: 5, richness: 2, fruitiness: 1, aroma: 3, aftertaste: 3 },
  { brandId: 'N0003', seriesId: 's-dessert', name: 'Twice Baked', desc: 'Social Smoke Twice Baked', sweetness: 5, sourness: 1, coolness: 1, richness: 5, fruitiness: 1, aroma: 4, aftertaste: 4 },
  { brandId: 'N0003', seriesId: 's-fruit-sour', name: 'Citrus Chill', desc: 'Social Smoke Citrus Chill', sweetness: 2, sourness: 4, coolness: 3, richness: 3, fruitiness: 4, aroma: 3, aftertaste: 3 },
  { brandId: 'N0003', seriesId: 's-fruit', name: 'Watermelon Chill', desc: 'Social Smoke Watermelon Chill', sweetness: 4, sourness: 1, coolness: 3, richness: 3, fruitiness: 4, aroma: 3, aftertaste: 3 },
  { brandId: 'N0003', seriesId: 's-fruit-sour', name: 'Lemon Drop', desc: 'Social Smoke Lemon Drop', sweetness: 2, sourness: 4, coolness: 1, richness: 3, fruitiness: 4, aroma: 3, aftertaste: 3 },
  { brandId: 'N0003', seriesId: 's-fruit', name: 'Passion Fruit Mojito', desc: 'Social Smoke Passion Fruit Mojito', sweetness: 3, sourness: 2, coolness: 2, richness: 3, fruitiness: 4, aroma: 4, aftertaste: 3 },
  { brandId: 'N0003', seriesId: 's-dessert', name: 'Coconut', desc: 'Social Smoke Coconut', sweetness: 4, sourness: 1, coolness: 1, richness: 4, fruitiness: 2, aroma: 4, aftertaste: 3 },
  { brandId: 'N0003', seriesId: 's-fruit', name: 'Peach', desc: 'Social Smoke Peach', sweetness: 4, sourness: 1, coolness: 1, richness: 3, fruitiness: 4, aroma: 3, aftertaste: 3 },
  { brandId: 'N0003', seriesId: 's-fruit', name: 'Mango Habanero', desc: 'Social Smoke Mango Habanero', sweetness: 3, sourness: 2, coolness: 1, richness: 3, fruitiness: 4, aroma: 4, aftertaste: 4 },
  { brandId: 'N0003', seriesId: 's-fruit', name: 'Grape', desc: 'Social Smoke Grape', sweetness: 4, sourness: 1, coolness: 1, richness: 3, fruitiness: 4, aroma: 3, aftertaste: 3 },
  { brandId: 'N0003', seriesId: 's-fruit-sour', name: 'Blue Raspberry', desc: 'Social Smoke Blue Raspberry', sweetness: 3, sourness: 3, coolness: 1, richness: 3, fruitiness: 4, aroma: 3, aftertaste: 3 },
  { brandId: 'N0003', seriesId: 's-fruit', name: 'Cantaloupe Chill', desc: 'Social Smoke Cantaloupe Chill', sweetness: 4, sourness: 1, coolness: 3, richness: 3, fruitiness: 4, aroma: 3, aftertaste: 3 },
  { brandId: 'N0003', seriesId: 's-fruit', name: 'White Peach', desc: 'Social Smoke White Peach', sweetness: 4, sourness: 1, coolness: 1, richness: 3, fruitiness: 4, aroma: 4, aftertaste: 3 },
  { brandId: 'N0003', seriesId: 's-fruit', name: 'Cherry', desc: 'Social Smoke Cherry', sweetness: 4, sourness: 2, coolness: 1, richness: 3, fruitiness: 4, aroma: 4, aftertaste: 3 },
  { brandId: 'N0003', seriesId: 's-fruit', name: 'Mango', desc: 'Social Smoke Mango', sweetness: 4, sourness: 2, coolness: 1, richness: 3, fruitiness: 5, aroma: 4, aftertaste: 3 },
  { brandId: 'N0003', seriesId: 's-fruit', name: 'Strawberry', desc: 'Social Smoke Strawberry', sweetness: 4, sourness: 2, coolness: 1, richness: 3, fruitiness: 4, aroma: 3, aftertaste: 3 },
  { brandId: 'N0003', seriesId: 's-mint', name: 'Mint', desc: 'Social Smoke Mint', sweetness: 1, sourness: 1, coolness: 4, richness: 3, fruitiness: 1, aroma: 3, aftertaste: 3 },
  // -- Tangiers --
  { brandId: 'N0004', seriesId: 's-fruit', name: 'Kashmir Peach', desc: 'Tangiers Kashmir Peach', sweetness: 4, sourness: 1, coolness: 1, richness: 4, fruitiness: 4, aroma: 4, aftertaste: 4 },
  { brandId: 'N0004', seriesId: 's-fruit', name: 'Kashmir Cherry', desc: 'Tangiers Kashmir Cherry', sweetness: 4, sourness: 2, coolness: 1, richness: 4, fruitiness: 4, aroma: 4, aftertaste: 4 },
  { brandId: 'N0004', seriesId: 's-fruit', name: 'Kashmir Apple', desc: 'Tangiers Kashmir Apple', sweetness: 3, sourness: 2, coolness: 1, richness: 4, fruitiness: 4, aroma: 4, aftertaste: 4 },
  { brandId: 'N0004', seriesId: 's-fruit', name: 'Kashmir Guajava', desc: 'Tangiers Kashmir Guajava', sweetness: 3, sourness: 2, coolness: 1, richness: 4, fruitiness: 4, aroma: 4, aftertaste: 4 },
  { brandId: 'N0004', seriesId: 's-cafe', name: 'Orange Soda', desc: 'Tangiers Orange Soda', sweetness: 4, sourness: 2, coolness: 1, richness: 3, fruitiness: 3, aroma: 3, aftertaste: 3 },
  { brandId: 'N0004', seriesId: 's-other', name: 'Static Starlight', desc: 'Tangiers Static Starlight', sweetness: 3, sourness: 1, coolness: 2, richness: 4, fruitiness: 2, aroma: 4, aftertaste: 4 },
  { brandId: 'N0004', seriesId: 's-other', name: 'Noir', desc: 'Tangiers Noir', sweetness: 2, sourness: 1, coolness: 1, richness: 5, fruitiness: 1, aroma: 5, aftertaste: 4 },
  { brandId: 'N0004', seriesId: 's-dessert', name: 'Blue Gumball 2.0', desc: 'Tangiers Blue Gumball 2.0', sweetness: 5, sourness: 1, coolness: 1, richness: 4, fruitiness: 3, aroma: 4, aftertaste: 4 },
  { brandId: 'N0004', seriesId: 's-dessert', name: 'Chocolate Iced Cream', desc: 'Tangiers Chocolate Iced Cream', sweetness: 5, sourness: 1, coolness: 2, richness: 5, fruitiness: 1, aroma: 4, aftertaste: 5 },
  { brandId: 'N0004', seriesId: 's-fruit', name: 'Foreplay on the Peach', desc: 'Tangiers Foreplay on the Peach', sweetness: 4, sourness: 1, coolness: 1, richness: 4, fruitiness: 4, aroma: 4, aftertaste: 4 },
  { brandId: 'N0004', seriesId: 's-fruit', name: 'Passionfruit', desc: 'Tangiers Passionfruit', sweetness: 3, sourness: 3, coolness: 1, richness: 3, fruitiness: 5, aroma: 4, aftertaste: 3 },
  { brandId: 'N0004', seriesId: 's-fruit', name: 'Maraschino Cherry', desc: 'Tangiers Maraschino Cherry', sweetness: 4, sourness: 2, coolness: 1, richness: 4, fruitiness: 4, aroma: 4, aftertaste: 4 },
  { brandId: 'N0004', seriesId: 's-other', name: 'Mime', desc: 'Tangiers Mime', sweetness: 2, sourness: 1, coolness: 3, richness: 4, fruitiness: 1, aroma: 4, aftertaste: 4 },
  { brandId: 'N0004', seriesId: 's-fruit-sour', name: 'New Lime', desc: 'Tangiers New Lime', sweetness: 2, sourness: 4, coolness: 1, richness: 3, fruitiness: 4, aroma: 3, aftertaste: 3 },
  { brandId: 'N0004', seriesId: 's-cafe', name: 'Peach Iced Tea', desc: 'Tangiers Peach Iced Tea', sweetness: 3, sourness: 2, coolness: 2, richness: 3, fruitiness: 3, aroma: 4, aftertaste: 3 },
  { brandId: 'N0004', seriesId: 's-cafe', name: 'Lemon Tea', desc: 'Tangiers Lemon Tea', sweetness: 2, sourness: 3, coolness: 1, richness: 3, fruitiness: 3, aroma: 4, aftertaste: 3 },
  { brandId: 'N0004', seriesId: 's-other', name: 'Ololiuqui', desc: 'Tangiers Ololiuqui', sweetness: 3, sourness: 2, coolness: 1, richness: 4, fruitiness: 2, aroma: 5, aftertaste: 4 },
  { brandId: 'N0004', seriesId: 's-fruit', name: 'Pineapple', desc: 'Tangiers Pineapple', sweetness: 4, sourness: 3, coolness: 1, richness: 3, fruitiness: 5, aroma: 4, aftertaste: 3 },
  { brandId: 'N0004', seriesId: 's-fruit', name: 'Watermelon', desc: 'Tangiers Watermelon', sweetness: 4, sourness: 1, coolness: 1, richness: 4, fruitiness: 4, aroma: 3, aftertaste: 3 },
  // -- Eternal Smoke --
  { brandId: 'N0005', seriesId: 's-fruit-sour', name: 'Lime Lit', desc: 'Eternal Smoke Lime Lit', sweetness: 2, sourness: 4, coolness: 1, richness: 3, fruitiness: 4, aroma: 3, aftertaste: 3 },
  { brandId: 'N0005', seriesId: 's-dessert', name: "Milkin' Cookies", desc: "Eternal Smoke Milkin' Cookies", sweetness: 5, sourness: 1, coolness: 1, richness: 5, fruitiness: 1, aroma: 4, aftertaste: 4 },
  { brandId: 'N0005', seriesId: 's-cafe', name: 'Chilled Wine', desc: 'Eternal Smoke Chilled Wine', sweetness: 3, sourness: 2, coolness: 2, richness: 4, fruitiness: 2, aroma: 4, aftertaste: 4 },
  { brandId: 'N0005', seriesId: 's-fruit', name: 'Blue Lit', desc: 'Eternal Smoke Blue Lit', sweetness: 4, sourness: 2, coolness: 1, richness: 3, fruitiness: 4, aroma: 3, aftertaste: 3 },
  { brandId: 'N0005', seriesId: 's-other', name: 'Morning Glory', desc: 'Eternal Smoke Morning Glory', sweetness: 3, sourness: 1, coolness: 2, richness: 3, fruitiness: 3, aroma: 4, aftertaste: 3 },
  { brandId: 'N0005', seriesId: 's-mint', name: 'Cool Kizz', desc: 'Eternal Smoke Cool Kizz', sweetness: 2, sourness: 1, coolness: 4, richness: 3, fruitiness: 2, aroma: 3, aftertaste: 3 },
  { brandId: 'N0005', seriesId: 's-other', name: 'Love 66', desc: 'Eternal Smoke Love 66', sweetness: 4, sourness: 2, coolness: 2, richness: 3, fruitiness: 3, aroma: 4, aftertaste: 4 },
  { brandId: 'N0005', seriesId: 's-fruit', name: 'Pearfection', desc: 'Eternal Smoke Pearfection', sweetness: 4, sourness: 2, coolness: 1, richness: 3, fruitiness: 4, aroma: 4, aftertaste: 3 },
  { brandId: 'N0005', seriesId: 's-fruit', name: 'Summer Berries', desc: 'Eternal Smoke Summer Berries', sweetness: 4, sourness: 2, coolness: 1, richness: 3, fruitiness: 4, aroma: 4, aftertaste: 3 },
  { brandId: 'N0005', seriesId: 's-fruit', name: 'Orange Lit', desc: 'Eternal Smoke Orange Lit', sweetness: 3, sourness: 3, coolness: 1, richness: 3, fruitiness: 4, aroma: 3, aftertaste: 3 },
  { brandId: 'N0005', seriesId: 's-fruit', name: 'Mango', desc: 'Eternal Smoke Mango', sweetness: 4, sourness: 2, coolness: 1, richness: 3, fruitiness: 5, aroma: 4, aftertaste: 3 },
  { brandId: 'N0005', seriesId: 's-fruit', name: 'Pineapple', desc: 'Eternal Smoke Pineapple', sweetness: 4, sourness: 3, coolness: 1, richness: 3, fruitiness: 5, aroma: 4, aftertaste: 3 },
  { brandId: 'N0005', seriesId: 's-fruit', name: 'Peach Lit', desc: 'Eternal Smoke Peach Lit', sweetness: 4, sourness: 1, coolness: 1, richness: 3, fruitiness: 4, aroma: 4, aftertaste: 3 },
  { brandId: 'N0005', seriesId: 's-fruit-sour', name: 'Lemon Lit', desc: 'Eternal Smoke Lemon Lit', sweetness: 2, sourness: 4, coolness: 1, richness: 3, fruitiness: 4, aroma: 3, aftertaste: 3 },
  { brandId: 'N0005', seriesId: 's-fruit', name: 'Grape', desc: 'Eternal Smoke Grape', sweetness: 4, sourness: 1, coolness: 1, richness: 3, fruitiness: 4, aroma: 3, aftertaste: 3 },
  { brandId: 'N0005', seriesId: 's-mint', name: 'Mint', desc: 'Eternal Smoke Mint', sweetness: 1, sourness: 1, coolness: 4, richness: 3, fruitiness: 1, aroma: 3, aftertaste: 3 },
  { brandId: 'N0005', seriesId: 's-fruit', name: 'Blueberry', desc: 'Eternal Smoke Blueberry', sweetness: 4, sourness: 2, coolness: 1, richness: 3, fruitiness: 4, aroma: 3, aftertaste: 3 },
  { brandId: 'N0005', seriesId: 's-fruit-sour', name: 'Kiwi', desc: 'Eternal Smoke Kiwi', sweetness: 3, sourness: 3, coolness: 1, richness: 3, fruitiness: 4, aroma: 3, aftertaste: 3 },
  // -- Azure --
  { brandId: 'N0006', seriesId: 's-dessert', name: 'Lemon Muffin', desc: 'Azure Lemon Muffin', sweetness: 4, sourness: 2, coolness: 1, richness: 4, fruitiness: 2, aroma: 4, aftertaste: 4 },
  { brandId: 'N0006', seriesId: 's-fruit', name: 'California Blue', desc: 'Azure California Blue', sweetness: 4, sourness: 2, coolness: 1, richness: 3, fruitiness: 4, aroma: 4, aftertaste: 3 },
  { brandId: 'N0006', seriesId: 's-fruit-sour', name: 'Royal Raspberry', desc: 'Azure Royal Raspberry', sweetness: 3, sourness: 3, coolness: 1, richness: 3, fruitiness: 4, aroma: 4, aftertaste: 3 },
  { brandId: 'N0006', seriesId: 's-fruit', name: 'Winter Peach', desc: 'Azure Winter Peach', sweetness: 4, sourness: 1, coolness: 2, richness: 3, fruitiness: 4, aroma: 4, aftertaste: 3 },
  { brandId: 'N0006', seriesId: 's-other', name: 'Cosmos', desc: 'Azure Cosmos', sweetness: 3, sourness: 2, coolness: 2, richness: 3, fruitiness: 3, aroma: 4, aftertaste: 3 },
  { brandId: 'N0006', seriesId: 's-dessert', name: 'Cherry Muffin', desc: 'Azure Cherry Muffin', sweetness: 5, sourness: 1, coolness: 1, richness: 4, fruitiness: 2, aroma: 4, aftertaste: 4 },
  { brandId: 'N0006', seriesId: 's-dessert', name: 'Mango Cheesecake', desc: 'Azure Mango Cheesecake', sweetness: 5, sourness: 1, coolness: 1, richness: 5, fruitiness: 3, aroma: 4, aftertaste: 4 },
  { brandId: 'N0006', seriesId: 's-fruit', name: 'Melon King', desc: 'Azure Melon King', sweetness: 5, sourness: 1, coolness: 1, richness: 3, fruitiness: 5, aroma: 4, aftertaste: 4 },
  { brandId: 'N0006', seriesId: 's-mint', name: 'Rio Mint', desc: 'Azure Rio Mint', sweetness: 1, sourness: 1, coolness: 4, richness: 3, fruitiness: 1, aroma: 3, aftertaste: 3 },
  { brandId: 'N0006', seriesId: 's-dessert', name: 'Cinnamon Cookies', desc: 'Azure Cinnamon Cookies', sweetness: 4, sourness: 1, coolness: 1, richness: 4, fruitiness: 1, aroma: 5, aftertaste: 4 },
  { brandId: 'N0006', seriesId: 's-dessert', name: 'Viva La Horchata', desc: 'Azure Viva La Horchata', sweetness: 4, sourness: 1, coolness: 1, richness: 5, fruitiness: 1, aroma: 4, aftertaste: 4 },
  { brandId: 'N0006', seriesId: 's-fruit', name: "Life's a Peach", desc: "Azure Life's a Peach", sweetness: 4, sourness: 1, coolness: 1, richness: 3, fruitiness: 4, aroma: 4, aftertaste: 3 },
  { brandId: 'N0006', seriesId: 's-other', name: 'Unicorn', desc: 'Azure Unicorn', sweetness: 5, sourness: 1, coolness: 1, richness: 3, fruitiness: 4, aroma: 4, aftertaste: 4 },
  { brandId: 'N0006', seriesId: 's-fruit', name: 'Berry Mania', desc: 'Azure Berry Mania', sweetness: 4, sourness: 2, coolness: 1, richness: 3, fruitiness: 5, aroma: 4, aftertaste: 3 },
  { brandId: 'N0006', seriesId: 's-fruit', name: 'Lychee', desc: 'Azure Lychee', sweetness: 4, sourness: 1, coolness: 1, richness: 3, fruitiness: 4, aroma: 4, aftertaste: 3 },
  { brandId: 'N0006', seriesId: 's-cafe', name: 'Apple Cider', desc: 'Azure Apple Cider', sweetness: 3, sourness: 2, coolness: 1, richness: 3, fruitiness: 3, aroma: 3, aftertaste: 3 },
  { brandId: 'N0006', seriesId: 's-other', name: 'Moscow Never Sleeps', desc: 'Azure Moscow Never Sleeps', sweetness: 2, sourness: 3, coolness: 3, richness: 3, fruitiness: 2, aroma: 3, aftertaste: 3 },
  { brandId: 'N0006', seriesId: 's-fruit', name: 'Pineapple Express', desc: 'Azure Pineapple Express', sweetness: 4, sourness: 3, coolness: 1, richness: 3, fruitiness: 5, aroma: 4, aftertaste: 3 },
  { brandId: 'N0006', seriesId: 's-fruit', name: 'Strawberry Passion', desc: 'Azure Strawberry Passion', sweetness: 4, sourness: 2, coolness: 1, richness: 3, fruitiness: 5, aroma: 4, aftertaste: 3 },
  { brandId: 'N0006', seriesId: 's-fruit-sour', name: 'Lime', desc: 'Azure Lime', sweetness: 2, sourness: 4, coolness: 1, richness: 3, fruitiness: 4, aroma: 3, aftertaste: 3 },
  // -- Trifecta --
  { brandId: 'N0007', seriesId: 's-mint', name: 'Peppermint Shake', desc: 'Trifecta Peppermint Shake', sweetness: 3, sourness: 1, coolness: 5, richness: 4, fruitiness: 1, aroma: 4, aftertaste: 4 },
  { brandId: 'N0007', seriesId: 's-mint', name: 'Mountain Fog', desc: 'Trifecta Mountain Fog', sweetness: 1, sourness: 1, coolness: 5, richness: 3, fruitiness: 1, aroma: 3, aftertaste: 3 },
  { brandId: 'N0007', seriesId: 's-fruit', name: 'Pineapple Guava', desc: 'Trifecta Pineapple Guava', sweetness: 4, sourness: 3, coolness: 1, richness: 3, fruitiness: 5, aroma: 4, aftertaste: 3 },
  { brandId: 'N0007', seriesId: 's-mint', name: 'Twice the Ice', desc: 'Trifecta Twice the Ice', sweetness: 1, sourness: 1, coolness: 5, richness: 2, fruitiness: 1, aroma: 3, aftertaste: 3 },
  { brandId: 'N0007', seriesId: 's-other', name: 'Bonafide', desc: 'Trifecta Bonafide', sweetness: 3, sourness: 2, coolness: 2, richness: 4, fruitiness: 2, aroma: 4, aftertaste: 4 },
  { brandId: 'N0007', seriesId: 's-other', name: 'TKO', desc: 'Trifecta TKO', sweetness: 3, sourness: 2, coolness: 2, richness: 4, fruitiness: 2, aroma: 4, aftertaste: 4 },
  { brandId: 'N0007', seriesId: 's-mint', name: 'Durty Mint', desc: 'Trifecta Durty Mint', sweetness: 2, sourness: 1, coolness: 4, richness: 4, fruitiness: 1, aroma: 4, aftertaste: 4 },
  { brandId: 'N0007', seriesId: 's-other', name: 'Enigma', desc: 'Trifecta Enigma', sweetness: 3, sourness: 1, coolness: 2, richness: 4, fruitiness: 2, aroma: 4, aftertaste: 4 },
  { brandId: 'N0007', seriesId: 's-other', name: 'Vertigo', desc: 'Trifecta Vertigo', sweetness: 3, sourness: 2, coolness: 3, richness: 4, fruitiness: 2, aroma: 4, aftertaste: 4 },
  { brandId: 'N0007', seriesId: 's-other', name: 'Nawar', desc: 'Trifecta Nawar', sweetness: 3, sourness: 1, coolness: 1, richness: 4, fruitiness: 2, aroma: 5, aftertaste: 4 },
  { brandId: 'N0007', seriesId: 's-mint', name: 'Mediterranean Mint', desc: 'Trifecta Mediterranean Mint', sweetness: 1, sourness: 1, coolness: 4, richness: 3, fruitiness: 1, aroma: 4, aftertaste: 3 },
  { brandId: 'N0007', seriesId: 's-fruit', name: 'Blue Strawberry', desc: 'Trifecta Blue Strawberry', sweetness: 4, sourness: 2, coolness: 1, richness: 3, fruitiness: 4, aroma: 4, aftertaste: 3 },
  { brandId: 'N0007', seriesId: 's-dessert', name: 'Lemon Pie', desc: 'Trifecta Lemon Pie', sweetness: 4, sourness: 3, coolness: 1, richness: 4, fruitiness: 2, aroma: 4, aftertaste: 4 },
  { brandId: 'N0007', seriesId: 's-other', name: 'Cucumber Mojito', desc: 'Trifecta Cucumber Mojito', sweetness: 2, sourness: 2, coolness: 3, richness: 3, fruitiness: 2, aroma: 4, aftertaste: 3 },
  { brandId: 'N0007', seriesId: 's-dessert', name: 'Indian Kheer', desc: 'Trifecta Indian Kheer', sweetness: 4, sourness: 1, coolness: 1, richness: 5, fruitiness: 1, aroma: 4, aftertaste: 5 },
  { brandId: 'N0007', seriesId: 's-fruit', name: 'Persian Melon', desc: 'Trifecta Persian Melon', sweetness: 5, sourness: 1, coolness: 1, richness: 3, fruitiness: 5, aroma: 4, aftertaste: 4 },
  { brandId: 'N0007', seriesId: 's-fruit', name: 'Grape', desc: 'Trifecta Grape', sweetness: 4, sourness: 1, coolness: 1, richness: 3, fruitiness: 4, aroma: 3, aftertaste: 3 },
  { brandId: 'N0007', seriesId: 's-fruit', name: 'Cherry Berry', desc: 'Trifecta Cherry Berry', sweetness: 4, sourness: 2, coolness: 1, richness: 3, fruitiness: 4, aroma: 4, aftertaste: 3 },
  { brandId: 'N0007', seriesId: 's-fruit', name: 'Orange Mint', desc: 'Trifecta Orange Mint', sweetness: 3, sourness: 2, coolness: 3, richness: 3, fruitiness: 4, aroma: 3, aftertaste: 3 },
  { brandId: 'N0007', seriesId: 's-fruit', name: 'Pineapple', desc: 'Trifecta Pineapple', sweetness: 4, sourness: 3, coolness: 1, richness: 3, fruitiness: 5, aroma: 4, aftertaste: 3 },
  // -- Afzal --
  { brandId: 'N0008', seriesId: 's-other', name: 'Pan Raas', desc: 'Afzal Pan Raas', sweetness: 3, sourness: 1, coolness: 1, richness: 4, fruitiness: 1, aroma: 5, aftertaste: 4 },
  { brandId: 'N0008', seriesId: 's-other', name: 'Bombay Pan Masala', desc: 'Afzal Bombay Pan Masala', sweetness: 2, sourness: 1, coolness: 1, richness: 4, fruitiness: 1, aroma: 5, aftertaste: 4 },
  { brandId: 'N0008', seriesId: 's-fruit-sour', name: 'Kiwi', desc: 'Afzal Kiwi', sweetness: 3, sourness: 3, coolness: 1, richness: 3, fruitiness: 4, aroma: 3, aftertaste: 3 },
  { brandId: 'N0008', seriesId: 's-mint', name: 'Mint', desc: 'Afzal Mint', sweetness: 1, sourness: 1, coolness: 4, richness: 3, fruitiness: 1, aroma: 3, aftertaste: 3 },
  { brandId: 'N0008', seriesId: 's-fruit', name: 'Grape Mint', desc: 'Afzal Grape Mint', sweetness: 3, sourness: 1, coolness: 3, richness: 3, fruitiness: 4, aroma: 3, aftertaste: 3 },
  { brandId: 'N0008', seriesId: 's-fruit-sour', name: 'Lemon Mint', desc: 'Afzal Lemon Mint', sweetness: 2, sourness: 4, coolness: 3, richness: 3, fruitiness: 4, aroma: 3, aftertaste: 3 },
  { brandId: 'N0008', seriesId: 's-fruit', name: 'Blueberry', desc: 'Afzal Blueberry', sweetness: 4, sourness: 2, coolness: 1, richness: 3, fruitiness: 4, aroma: 3, aftertaste: 3 },
  { brandId: 'N0008', seriesId: 's-fruit', name: 'Strawberry', desc: 'Afzal Strawberry', sweetness: 4, sourness: 2, coolness: 1, richness: 3, fruitiness: 4, aroma: 3, aftertaste: 3 },
  { brandId: 'N0008', seriesId: 's-fruit', name: 'Mango', desc: 'Afzal Mango', sweetness: 4, sourness: 2, coolness: 1, richness: 3, fruitiness: 5, aroma: 4, aftertaste: 3 },
  { brandId: 'N0008', seriesId: 's-fruit', name: 'Orange', desc: 'Afzal Orange', sweetness: 3, sourness: 3, coolness: 1, richness: 3, fruitiness: 4, aroma: 3, aftertaste: 3 },
  { brandId: 'N0008', seriesId: 's-fruit', name: 'Peach', desc: 'Afzal Peach', sweetness: 4, sourness: 1, coolness: 1, richness: 3, fruitiness: 4, aroma: 3, aftertaste: 3 },
  { brandId: 'N0008', seriesId: 's-fruit', name: 'Watermelon', desc: 'Afzal Watermelon', sweetness: 4, sourness: 1, coolness: 1, richness: 3, fruitiness: 4, aroma: 3, aftertaste: 3 },
  { brandId: 'N0008', seriesId: 's-fruit', name: 'Guava', desc: 'Afzal Guava', sweetness: 4, sourness: 2, coolness: 1, richness: 3, fruitiness: 4, aroma: 3, aftertaste: 3 },
  { brandId: 'N0008', seriesId: 's-fruit', name: 'Lychee', desc: 'Afzal Lychee', sweetness: 4, sourness: 1, coolness: 1, richness: 3, fruitiness: 4, aroma: 4, aftertaste: 3 },
  { brandId: 'N0008', seriesId: 's-cafe', name: 'Cola', desc: 'Afzal Cola', sweetness: 4, sourness: 2, coolness: 2, richness: 3, fruitiness: 1, aroma: 4, aftertaste: 3 },
  { brandId: 'N0008', seriesId: 's-spice', name: 'Cinnamon Gum', desc: 'Afzal Cinnamon Gum', sweetness: 3, sourness: 1, coolness: 1, richness: 4, fruitiness: 1, aroma: 5, aftertaste: 4 },
  { brandId: 'N0008', seriesId: 's-other', name: 'Paan Twist', desc: 'Afzal Paan Twist', sweetness: 3, sourness: 1, coolness: 1, richness: 4, fruitiness: 1, aroma: 5, aftertaste: 4 },
  { brandId: 'N0008', seriesId: 's-fruit', name: 'Double Apple', desc: 'Afzal Double Apple', sweetness: 4, sourness: 1, coolness: 1, richness: 4, fruitiness: 4, aroma: 4, aftertaste: 4 },
  { brandId: 'N0008', seriesId: 's-other', name: 'Cocktail', desc: 'Afzal Cocktail', sweetness: 4, sourness: 2, coolness: 2, richness: 3, fruitiness: 3, aroma: 4, aftertaste: 3 },
  { brandId: 'N0008', seriesId: 's-other', name: 'Magic Love', desc: 'Afzal Magic Love', sweetness: 4, sourness: 2, coolness: 2, richness: 3, fruitiness: 3, aroma: 4, aftertaste: 3 },
  // -- Nakhla --
  { brandId: 'N0009', seriesId: 's-fruit', name: 'Double Apple', desc: 'Nakhla Double Apple', sweetness: 4, sourness: 1, coolness: 1, richness: 5, fruitiness: 4, aroma: 4, aftertaste: 4 },
  { brandId: 'N0009', seriesId: 's-mint', name: 'Mizo Mint', desc: 'Nakhla Mizo Mint', sweetness: 1, sourness: 1, coolness: 5, richness: 3, fruitiness: 1, aroma: 3, aftertaste: 3 },
  { brandId: 'N0009', seriesId: 's-fruit-sour', name: 'Lemon', desc: 'Nakhla Lemon', sweetness: 2, sourness: 4, coolness: 1, richness: 3, fruitiness: 4, aroma: 3, aftertaste: 3 },
  { brandId: 'N0009', seriesId: 's-fruit', name: 'Cherry', desc: 'Nakhla Cherry', sweetness: 4, sourness: 2, coolness: 1, richness: 3, fruitiness: 4, aroma: 4, aftertaste: 3 },
  { brandId: 'N0009', seriesId: 's-fruit', name: 'Peach', desc: 'Nakhla Peach', sweetness: 4, sourness: 1, coolness: 1, richness: 3, fruitiness: 4, aroma: 3, aftertaste: 3 },
  { brandId: 'N0009', seriesId: 's-fruit', name: 'Orange', desc: 'Nakhla Orange', sweetness: 3, sourness: 3, coolness: 1, richness: 3, fruitiness: 4, aroma: 3, aftertaste: 3 },
  { brandId: 'N0009', seriesId: 's-fruit', name: 'Grape', desc: 'Nakhla Grape', sweetness: 4, sourness: 1, coolness: 1, richness: 3, fruitiness: 4, aroma: 3, aftertaste: 3 },
  { brandId: 'N0009', seriesId: 's-fruit', name: 'Watermelon', desc: 'Nakhla Watermelon', sweetness: 4, sourness: 1, coolness: 1, richness: 3, fruitiness: 4, aroma: 3, aftertaste: 3 },
  { brandId: 'N0009', seriesId: 's-fruit', name: 'Mango', desc: 'Nakhla Mango', sweetness: 4, sourness: 2, coolness: 1, richness: 3, fruitiness: 5, aroma: 4, aftertaste: 3 },
  { brandId: 'N0009', seriesId: 's-fruit', name: 'Blueberry', desc: 'Nakhla Blueberry', sweetness: 4, sourness: 2, coolness: 1, richness: 3, fruitiness: 4, aroma: 3, aftertaste: 3 },
  { brandId: 'N0009', seriesId: 's-cafe', name: 'Cappuccino', desc: 'Nakhla Cappuccino', sweetness: 3, sourness: 1, coolness: 1, richness: 4, fruitiness: 1, aroma: 4, aftertaste: 4 },
  { brandId: 'N0009', seriesId: 's-cafe', name: 'Coffee', desc: 'Nakhla Coffee', sweetness: 2, sourness: 1, coolness: 1, richness: 4, fruitiness: 1, aroma: 5, aftertaste: 4 },
  { brandId: 'N0009', seriesId: 's-spice', name: 'Cinnamon', desc: 'Nakhla Cinnamon', sweetness: 3, sourness: 1, coolness: 1, richness: 4, fruitiness: 1, aroma: 5, aftertaste: 4 },
  { brandId: 'N0009', seriesId: 's-other', name: 'Jasmine', desc: 'Nakhla Jasmine', sweetness: 2, sourness: 1, coolness: 1, richness: 3, fruitiness: 1, aroma: 5, aftertaste: 4 },
  { brandId: 'N0009', seriesId: 's-fruit', name: 'Guava', desc: 'Nakhla Guava', sweetness: 4, sourness: 2, coolness: 1, richness: 3, fruitiness: 4, aroma: 3, aftertaste: 3 },
  { brandId: 'N0009', seriesId: 's-fruit', name: 'Melon', desc: 'Nakhla Melon', sweetness: 4, sourness: 1, coolness: 1, richness: 3, fruitiness: 4, aroma: 3, aftertaste: 3 },
  { brandId: 'N0009', seriesId: 's-fruit', name: 'Strawberry', desc: 'Nakhla Strawberry', sweetness: 4, sourness: 2, coolness: 1, richness: 3, fruitiness: 4, aroma: 3, aftertaste: 3 },
  { brandId: 'N0009', seriesId: 's-mint', name: 'Mint', desc: 'Nakhla Mint', sweetness: 1, sourness: 1, coolness: 4, richness: 3, fruitiness: 1, aroma: 3, aftertaste: 3 },
  { brandId: 'N0009', seriesId: 's-fruit-sour', name: 'Lemon Mint', desc: 'Nakhla Lemon Mint', sweetness: 2, sourness: 4, coolness: 3, richness: 3, fruitiness: 4, aroma: 3, aftertaste: 3 },
  // -- Dozaji --
  { brandId: 'N0011', seriesId: 's-other', name: 'Love 66', desc: 'Dozaji Love 66', sweetness: 4, sourness: 2, coolness: 2, richness: 3, fruitiness: 3, aroma: 4, aftertaste: 4 },
  { brandId: 'N0011', seriesId: 's-fruit-sour', name: 'Ice Grapefruit', desc: 'Dozaji Ice Grapefruit', sweetness: 2, sourness: 4, coolness: 3, richness: 3, fruitiness: 4, aroma: 3, aftertaste: 3 },
  { brandId: 'N0011', seriesId: 's-other', name: 'Blue Mist', desc: 'Dozaji Blue Mist', sweetness: 3, sourness: 1, coolness: 4, richness: 3, fruitiness: 2, aroma: 3, aftertaste: 3 },
  { brandId: 'N0011', seriesId: 's-other', name: 'Lady Killer', desc: 'Dozaji Lady Killer', sweetness: 4, sourness: 2, coolness: 2, richness: 3, fruitiness: 3, aroma: 4, aftertaste: 4 },
  { brandId: 'N0011', seriesId: 's-fruit', name: 'Watermelon Ice', desc: 'Dozaji Watermelon Ice', sweetness: 4, sourness: 1, coolness: 3, richness: 3, fruitiness: 4, aroma: 3, aftertaste: 3 },
  { brandId: 'N0011', seriesId: 's-other', name: 'Mi Amor', desc: 'Dozaji Mi Amor', sweetness: 4, sourness: 2, coolness: 1, richness: 3, fruitiness: 3, aroma: 4, aftertaste: 4 },
  { brandId: 'N0011', seriesId: 's-fruit-sour', name: 'Lemon Mint', desc: 'Dozaji Lemon Mint', sweetness: 2, sourness: 4, coolness: 3, richness: 3, fruitiness: 4, aroma: 3, aftertaste: 3 },
  { brandId: 'N0011', seriesId: 's-fruit', name: 'Pineapple', desc: 'Dozaji Pineapple', sweetness: 4, sourness: 3, coolness: 1, richness: 3, fruitiness: 5, aroma: 4, aftertaste: 3 },
  { brandId: 'N0011', seriesId: 's-fruit', name: 'Mango Tango', desc: 'Dozaji Mango Tango', sweetness: 4, sourness: 2, coolness: 1, richness: 3, fruitiness: 5, aroma: 4, aftertaste: 3 },
  { brandId: 'N0011', seriesId: 's-fruit', name: 'Grape Mint', desc: 'Dozaji Grape Mint', sweetness: 3, sourness: 1, coolness: 3, richness: 3, fruitiness: 4, aroma: 3, aftertaste: 3 },
  { brandId: 'N0011', seriesId: 's-fruit-sour', name: 'Kiwi', desc: 'Dozaji Kiwi', sweetness: 3, sourness: 3, coolness: 1, richness: 3, fruitiness: 4, aroma: 3, aftertaste: 3 },
  { brandId: 'N0011', seriesId: 's-fruit', name: 'Blueberry', desc: 'Dozaji Blueberry', sweetness: 4, sourness: 2, coolness: 1, richness: 3, fruitiness: 4, aroma: 3, aftertaste: 3 },
  { brandId: 'N0011', seriesId: 's-fruit', name: 'Orange', desc: 'Dozaji Orange', sweetness: 3, sourness: 3, coolness: 1, richness: 3, fruitiness: 4, aroma: 3, aftertaste: 3 },
  { brandId: 'N0011', seriesId: 's-fruit', name: 'Peach', desc: 'Dozaji Peach', sweetness: 4, sourness: 1, coolness: 1, richness: 3, fruitiness: 4, aroma: 3, aftertaste: 3 },
  { brandId: 'N0011', seriesId: 's-fruit', name: 'Cherry', desc: 'Dozaji Cherry', sweetness: 4, sourness: 2, coolness: 1, richness: 3, fruitiness: 4, aroma: 4, aftertaste: 3 },
  { brandId: 'N0011', seriesId: 's-mint', name: 'Mint', desc: 'Dozaji Mint', sweetness: 1, sourness: 1, coolness: 4, richness: 3, fruitiness: 1, aroma: 3, aftertaste: 3 },
  { brandId: 'N0011', seriesId: 's-other', name: 'Gum Mint', desc: 'Dozaji Gum Mint', sweetness: 3, sourness: 1, coolness: 4, richness: 3, fruitiness: 1, aroma: 3, aftertaste: 3 },
  { brandId: 'N0011', seriesId: 's-fruit', name: 'Melon', desc: 'Dozaji Melon', sweetness: 4, sourness: 1, coolness: 1, richness: 3, fruitiness: 4, aroma: 3, aftertaste: 3 },
  { brandId: 'N0011', seriesId: 's-fruit', name: 'Passion Fruit', desc: 'Dozaji Passion Fruit', sweetness: 3, sourness: 3, coolness: 1, richness: 3, fruitiness: 5, aroma: 4, aftertaste: 3 },
  { brandId: 'N0011', seriesId: 's-fruit', name: 'Two Apples', desc: 'Dozaji Two Apples', sweetness: 4, sourness: 1, coolness: 1, richness: 4, fruitiness: 4, aroma: 4, aftertaste: 4 },
  // -- Debaji --
  { brandId: 'N0012', seriesId: 's-other', name: 'Love 66', desc: 'Debaji Love 66', sweetness: 4, sourness: 2, coolness: 2, richness: 3, fruitiness: 3, aroma: 4, aftertaste: 4 },
  { brandId: 'N0012', seriesId: 's-other', name: 'Lady Killer', desc: 'Debaji Lady Killer', sweetness: 4, sourness: 2, coolness: 2, richness: 3, fruitiness: 3, aroma: 4, aftertaste: 4 },
  { brandId: 'N0012', seriesId: 's-other', name: 'Blue Mist', desc: 'Debaji Blue Mist', sweetness: 3, sourness: 1, coolness: 4, richness: 3, fruitiness: 2, aroma: 3, aftertaste: 3 },
  { brandId: 'N0012', seriesId: 's-fruit', name: 'Grape Mint', desc: 'Debaji Grape Mint', sweetness: 3, sourness: 1, coolness: 3, richness: 3, fruitiness: 4, aroma: 3, aftertaste: 3 },
  { brandId: 'N0012', seriesId: 's-fruit-sour', name: 'Lemon Mint', desc: 'Debaji Lemon Mint', sweetness: 2, sourness: 4, coolness: 3, richness: 3, fruitiness: 4, aroma: 3, aftertaste: 3 },
  { brandId: 'N0012', seriesId: 's-fruit', name: 'Watermelon Ice', desc: 'Debaji Watermelon Ice', sweetness: 4, sourness: 1, coolness: 3, richness: 3, fruitiness: 4, aroma: 3, aftertaste: 3 },
  { brandId: 'N0012', seriesId: 's-fruit', name: 'Mango', desc: 'Debaji Mango', sweetness: 4, sourness: 2, coolness: 1, richness: 3, fruitiness: 5, aroma: 4, aftertaste: 3 },
  { brandId: 'N0012', seriesId: 's-fruit', name: 'Pineapple', desc: 'Debaji Pineapple', sweetness: 4, sourness: 3, coolness: 1, richness: 3, fruitiness: 5, aroma: 4, aftertaste: 3 },
  { brandId: 'N0012', seriesId: 's-fruit', name: 'Peach', desc: 'Debaji Peach', sweetness: 4, sourness: 1, coolness: 1, richness: 3, fruitiness: 4, aroma: 3, aftertaste: 3 },
  { brandId: 'N0012', seriesId: 's-fruit', name: 'Orange', desc: 'Debaji Orange', sweetness: 3, sourness: 3, coolness: 1, richness: 3, fruitiness: 4, aroma: 3, aftertaste: 3 },
  { brandId: 'N0012', seriesId: 's-fruit', name: 'Blueberry', desc: 'Debaji Blueberry', sweetness: 4, sourness: 2, coolness: 1, richness: 3, fruitiness: 4, aroma: 3, aftertaste: 3 },
  { brandId: 'N0012', seriesId: 's-fruit', name: 'Cherry', desc: 'Debaji Cherry', sweetness: 4, sourness: 2, coolness: 1, richness: 3, fruitiness: 4, aroma: 4, aftertaste: 3 },
  { brandId: 'N0012', seriesId: 's-mint', name: 'Mint', desc: 'Debaji Mint', sweetness: 1, sourness: 1, coolness: 4, richness: 3, fruitiness: 1, aroma: 3, aftertaste: 3 },
  { brandId: 'N0012', seriesId: 's-fruit-sour', name: 'Kiwi', desc: 'Debaji Kiwi', sweetness: 3, sourness: 3, coolness: 1, richness: 3, fruitiness: 4, aroma: 3, aftertaste: 3 },
  { brandId: 'N0012', seriesId: 's-fruit', name: 'Melon', desc: 'Debaji Melon', sweetness: 4, sourness: 1, coolness: 1, richness: 3, fruitiness: 4, aroma: 3, aftertaste: 3 },
  { brandId: 'N0012', seriesId: 's-fruit', name: 'Passion Fruit', desc: 'Debaji Passion Fruit', sweetness: 3, sourness: 3, coolness: 1, richness: 3, fruitiness: 5, aroma: 4, aftertaste: 3 },
  { brandId: 'N0012', seriesId: 's-other', name: 'Gum Mint', desc: 'Debaji Gum Mint', sweetness: 3, sourness: 1, coolness: 4, richness: 3, fruitiness: 1, aroma: 3, aftertaste: 3 },
  { brandId: 'N0012', seriesId: 's-fruit', name: 'Two Apples', desc: 'Debaji Two Apples', sweetness: 4, sourness: 1, coolness: 1, richness: 4, fruitiness: 4, aroma: 4, aftertaste: 4 },
  { brandId: 'N0012', seriesId: 's-fruit', name: 'Strawberry', desc: 'Debaji Strawberry', sweetness: 4, sourness: 2, coolness: 1, richness: 3, fruitiness: 4, aroma: 3, aftertaste: 3 },
  { brandId: 'N0012', seriesId: 's-other', name: 'Magic Love', desc: 'Debaji Magic Love', sweetness: 4, sourness: 2, coolness: 2, richness: 3, fruitiness: 3, aroma: 4, aftertaste: 3 },
];


import { db } from './db';

export async function generateNextId(table: any, prefix: string): Promise<string> {
  const items = await table.toArray();
  let maxNum = 0;
  for (const item of items) {
    if (item.id && item.id.startsWith(prefix)) {
      const numStr = item.id.substring(prefix.length);
      const num = parseInt(numStr, 10);
      if (!isNaN(num) && num > maxNum) {
        maxNum = num;
      }
    }
  }
  maxNum++;
  return `${prefix}${String(maxNum).padStart(4, '0')}`;
}

export async function generateBrandId(): Promise<string> {
  return await generateNextId(db.brands, 'N');
}

export async function generateFlavorId(): Promise<string> {
  return await generateNextId(db.flavors, 'FL');
}

export async function generateMixId(): Promise<string> {
  return await generateNextId(db.mixes, 'RE');
}


export class LocalDatabase {
  static async init() {
    // Migration: Clean up old duplicate presets
    const allFlavors = await db.flavors.toArray();
    const oldFlavors = allFlavors.filter(f => /^f-\d+$/.test(f.id));
    if (oldFlavors.length > 0) {
      for (const oldF of oldFlavors) {
        const numStr = oldF.id.replace('f-', '');
        const newFid = `FL${numStr.padStart(4, '0')}`;
        await db.userFlavors.where('flavor_id').equals(oldF.id).modify({ flavor_id: newFid });
        await db.mixFlavors.where('flavor_id').equals(oldF.id).modify({ flavor_id: newFid });
      }
      await db.flavors.bulkDelete(oldFlavors.map(f => f.id));
      await db.flavorTasteMaster.bulkDelete(oldFlavors.map(f => f.id));
    }

    const allBrands = await db.brands.toArray();
    const oldBrands = allBrands.filter(b => /^b-[a-z]+$/.test(b.id));
    if (oldBrands.length > 0) {
      const newBrands = allBrands.filter(b => /^N\d{4}$/.test(b.id));
      for (const oldB of oldBrands) {
        const newB = newBrands.find(nb => nb.name === oldB.name) || DEFAULT_BRANDS.find(db => db.name === oldB.name);
        if (newB) {
          await db.flavors.where('brand_id').equals(oldB.id).modify({ brand_id: newB.id });
        }
      }
      await db.brands.bulkDelete(oldBrands.map(b => b.id));
    }

    const brandCount = await db.brands.count();
    if (brandCount === 0) {
      await db.brands.bulkPut(DEFAULT_BRANDS);
      await db.series.bulkPut(DEFAULT_SERIES);
      
      const flavors: DbFlavor[] = [];
      const tasteMasters: DbFlavorTasteVectorMaster[] = [];

      POPULAR_FLAVORS_MAPPING.forEach((f, index) => {
        const fid = `FL${String(index + 1).padStart(4, '0')}`;
        flavors.push({
          id: fid,
          brand_id: f.brandId,
          name: f.name,
          series_id: f.seriesId,
          description: f.desc,
          nicotine: (f as any).nicotine ?? true,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        });

        tasteMasters.push({
          flavor_id: fid,
          sweetness: f.sweetness,
          sourness: f.sourness,
          coolness: f.coolness,
          richness: f.richness,
          fruitiness: f.fruitiness,
          aroma: f.aroma,
          aftertaste: f.aftertaste,
        });
      });

      await db.flavors.bulkPut(flavors);
      await db.flavorTasteMaster.bulkPut(tasteMasters);
    } else {
      const existingBrands = await db.brands.toArray();
      if (existingBrands.length < DEFAULT_BRANDS.length) {
        await db.brands.bulkPut(DEFAULT_BRANDS);
      }
      
      const lastPresetId = `FL${String(POPULAR_FLAVORS_MAPPING.length).padStart(4, '0')}`;
      const lastPresetExists = await db.flavors.get(lastPresetId);
      
      if (!lastPresetExists) {
        const existingFlavors = await db.flavors.toArray();
        const existingIds = new Set(existingFlavors.map(f => f.id));
        
        const newFlavors: DbFlavor[] = [];
        const newTasteMasters: DbFlavorTasteVectorMaster[] = [];

        POPULAR_FLAVORS_MAPPING.forEach((f, index) => {
          const fid = `FL${String(index + 1).padStart(4, '0')}`;
          if (!existingIds.has(fid)) {
            newFlavors.push({
              id: fid,
              brand_id: f.brandId,
              name: f.name,
              series_id: f.seriesId,
              description: f.desc,
              nicotine: (f as any).nicotine ?? true,
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString(),
            });
            newTasteMasters.push({
              flavor_id: fid,
              sweetness: f.sweetness,
              sourness: f.sourness,
              coolness: f.coolness,
              richness: f.richness,
              fruitiness: f.fruitiness,
              aroma: f.aroma,
              aftertaste: f.aftertaste,
            });
          }
        });

        if (newFlavors.length > 0) {
          await db.flavors.bulkPut(newFlavors);
          await db.flavorTasteMaster.bulkPut(newTasteMasters);
        }
      }
    }
  }
}

// ----------------------------------------------------
// Repositories Implementations
// ----------------------------------------------------

export type TasteVector4 = {
  sweetness: number;
  sourness: number;
  coolness: number;
  fruitiness: number;
};

export interface FlavorDetails {
  id: string;
  brand_id: string;
  brand_name: string;
  name: string;
  series_id?: string;
  series_name?: string;
  description?: string;
  owned: boolean;
  rating?: number;
  memo?: string;
  nicotine: boolean;
  /** マスターデータが存在する場合はセットされる（自作フレーバーは undefined） */
  master_taste_vector?: TasteVector4;
  /** ユーザーが個人評価を入力済みの場合はセットされる */
  user_taste_vector?: TasteVector4;
  /** 後方互換: user優先、なければmaster、なければデフォルト3 */
  taste_vector: TasteVector4;
}

export class FlavorRepository {
  static async getAll(): Promise<FlavorDetails[]> {
    await LocalDatabase.init();
    
    const flavors = await db.flavors.toArray();
    const brands = await db.brands.toArray();
    const series = await db.series.toArray();
    const userFlavors = await db.userFlavors.where('user_id').equals('local-user').toArray();
    const userTastes = await db.userFlavorTaste.toArray();
    const masterTastes = await db.flavorTasteMaster.toArray();

    return flavors.map((f) => {
      const brand = brands.find((b) => b.id === f.brand_id);
      const ser = series.find((s) => s.id === f.series_id);
      const uf = userFlavors.find((u) => u.flavor_id === f.id);
      const ut = uf ? userTastes.find((t) => t.user_flavor_id === uf.id) : undefined;
      const mt = masterTastes.find((t) => t.flavor_id === f.id);

      const master_taste_vector: TasteVector4 | undefined = mt
        ? {
            sweetness: mt.sweetness,
            sourness: mt.sourness,
            coolness: mt.coolness,
            fruitiness: mt.fruitiness,
          }
        : undefined;

      const user_taste_vector: TasteVector4 | undefined = ut
        ? {
            sweetness: ut.sweetness,
            sourness: ut.sourness,
            coolness: ut.coolness,
            fruitiness: ut.fruitiness,
          }
        : undefined;

      // 後方互換: user優先、なければmaster、なければデフォルト3
      const taste_vector: TasteVector4 = {
        sweetness: ut ? ut.sweetness : mt ? mt.sweetness : 3,
        sourness: ut ? ut.sourness : mt ? mt.sourness : 3,
        coolness: ut ? ut.coolness : mt ? mt.coolness : 3,
        fruitiness: ut ? ut.fruitiness : mt ? mt.fruitiness : 3,
      };

      return {
        id: f.id,
        brand_id: f.brand_id,
        brand_name: brand ? brand.name : 'Unknown',
        name: f.name,
        series_id: f.series_id,
        series_name: ser ? ser.name : undefined,
        description: f.description,
        owned: uf ? uf.owned : false,
        rating: uf ? uf.rating : undefined,
        memo: uf ? uf.memo : undefined,
        nicotine: f.nicotine !== false,
        master_taste_vector,
        user_taste_vector,
        taste_vector,
      };
    });
  }

  static async getById(id: string): Promise<FlavorDetails | null> {
    const list = await this.getAll();
    return list.find((f) => f.id === id || f.id.startsWith(id)) || null;
  }

  static async saveUserFlavor(
    flavorId: string,
    owned: boolean,
    rating?: number,
    memo?: string,
    tasteVector?: FlavorDetails['taste_vector'],
    nicotine?: boolean,
  ): Promise<void> {
    let uf = await db.userFlavors.where({ user_id: 'local-user', flavor_id: flavorId }).first();

    if (!uf) {
      uf = {
        id: generateULID(),
        user_id: 'local-user',
        flavor_id: flavorId,
        owned,
        rating,
        memo,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };
      await db.userFlavors.add(uf);
    } else {
      await db.userFlavors.update(uf.id, {
        owned,
        rating,
        memo,
        updated_at: new Date().toISOString()
      });
    }

    if (nicotine !== undefined) {
      await db.flavors.update(flavorId, { nicotine, updated_at: new Date().toISOString() });
    }

    if (tasteVector) {
      let ut = await db.userFlavorTaste.where('user_flavor_id').equals(uf.id).first();
      if (!ut) {
        await db.userFlavorTaste.add({
          user_flavor_id: uf.id,
          ...tasteVector,
          richness: 3, aroma: 3, aftertaste: 3
        });
      } else {
        await db.userFlavorTaste.update(uf.id, { ...tasteVector });
      }
    }
  }

  static async toggleOwnership(flavorId: string): Promise<boolean> {
    const details = await this.getById(flavorId);
    if (!details) throw new Error('Flavor not found');
    const newOwned = !details.owned;
    await this.saveUserFlavor(flavorId, newOwned, details.rating, details.memo, details.user_taste_vector, details.nicotine);
    return newOwned;
  }

  static async addCustomFlavor(
    brandName: string,
    flavorName: string,
    seriesId: string,
    description: string,
    tasteVector?: any,
    nicotine?: boolean,
  ): Promise<{id: string}> {
    let brand = await db.brands.where('name').equalsIgnoreCase(brandName).first();
    let brandId = brand?.id;

    if (!brandId) {
      brandId = await generateBrandId();
      await db.brands.add({
        id: brandId,
        name: brandName,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      });
    }

    const flavorId = await generateFlavorId();
    await db.flavors.add({
      id: flavorId,
      brand_id: brandId,
      name: flavorName,
      series_id: seriesId,
      description,
      nicotine: nicotine !== false,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    });

    return { id: flavorId };
  }

  static async bulkSetUnowned(flavorIds: string[]): Promise<void> {
    const userFlavors = await db.userFlavors
      .where('user_id')
      .equals('local-user')
      .toArray();

    const toUpdate = userFlavors.filter((uf) => flavorIds.includes(uf.flavor_id) && uf.owned);
    if (toUpdate.length === 0) return;

    for (const uf of toUpdate) {
      await db.userFlavors.update(uf.id, {
        owned: false,
        updated_at: new Date().toISOString()
      });
    }
  }
}

export interface MixDetails {
  id: string;
  user_id: string;
  name: string;
  impression?: string;
  rating?: number;
  is_public: boolean;
  is_favorite?: boolean;
  created_at: string;
  flavors: {
    flavor_id: string;
    custom_flavor_name?: string;
    brand_name: string;
    flavor_name: string;
    ratio: number;
    color: string;
  }[];
}

export class MixRepository {
  static async getAll(): Promise<MixDetails[]> {
    await LocalDatabase.init();
    
    const mixes = await db.mixes.toArray();
    const mixFlavors = await db.mixFlavors.toArray();
    const flavors = await db.flavors.toArray();
    const brands = await db.brands.toArray();

    return mixes.map((m) => {
      const mfs = mixFlavors.filter((mf) => mf.mix_id === m.id).sort((a, b) => a.sort_order - b.sort_order);
      const mappedFlavors = mfs.map((mf) => {
        const f = flavors.find((fl) => fl.id === mf.flavor_id);
        const b = f ? brands.find((br) => br.id === f.brand_id) : undefined;
        return {
          flavor_id: mf.flavor_id,
          custom_flavor_name: mf.custom_flavor_name,
          brand_name: b ? b.name : (mf.custom_flavor_name ? '' : 'Unknown'),
          flavor_name: f ? f.name : (mf.custom_flavor_name || 'Unknown'),
          ratio: mf.ratio,
          color: getFlavorColor(b ? b.name : '', f ? f.name : ''),
        };
      });

      return {
        ...m,
        is_favorite: m.is_favorite ?? false,
        flavors: mappedFlavors,
      };
    }).sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
  }

  static async getById(id: string): Promise<MixDetails | null> {
    const list = await this.getAll();
    return list.find((m) => m.id === id || m.id.startsWith(id)) || null;
  }

  static async save(
    options: {
      name: string;
      impression?: string;
      rating?: number;
      is_public?: boolean;
    },
    flavorsData: { flavor_id: string; custom_flavor_name?: string; ratio: number }[],
    mixId?: string,
  ): Promise<MixDetails> {
    const isNew = !mixId;
    const finalMixId = mixId || await generateMixId();

    if (isNew) {
      await db.mixes.add({
        id: finalMixId,
        user_id: 'local-user',
        name: options.name,
        impression: options.impression,
        rating: options.rating,
        is_public: options.is_public ?? true,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      });
    } else {
      await db.mixes.update(finalMixId, {
        ...options,
        updated_at: new Date().toISOString(),
      });
      // Delete old mix flavors
      await db.mixFlavors.where('mix_id').equals(finalMixId).delete();
    }

    // Add new mix flavors
    const mfs = flavorsData.map((fd, idx) => ({
      id: generateULID(),
      mix_id: finalMixId,
      flavor_id: fd.flavor_id,
      custom_flavor_name: fd.custom_flavor_name,
      ratio: fd.ratio,
      sort_order: idx + 1,
    }));
    await db.mixFlavors.bulkAdd(mfs);

    const details = await this.getById(finalMixId);
    if (!details) throw new Error('Failed to retrieve newly saved mix');
    return details;
  }

  static async delete(id: string): Promise<void> {
    await db.mixes.delete(id);
    await db.mixFlavors.where('mix_id').equals(id).delete();
  }

  static async toggleFavorite(id: string): Promise<boolean> {
    const mix = await db.mixes.get(id);
    if (!mix) throw new Error('Mix not found');
    const newValue = !(mix.is_favorite ?? false);
    await db.mixes.update(id, { is_favorite: newValue, updated_at: new Date().toISOString() });
    return newValue;
  }
}

export class BrandRepository {
  static async getAll(): Promise<DbBrand[]> {
    await LocalDatabase.init();
    return await db.brands.toArray();
  }
}

export class SeriesRepository {
  static async getAll(): Promise<DbSeries[]> {
    await LocalDatabase.init();
    return await db.series.toArray();
  }
}

// Helper to assign a color based on flavor name or brand
export function getFlavorColor(brand: string, name: string): string {
  const normName = name.toLowerCase();
  if (normName.includes('apple')) return '#d62222';
  if (normName.includes('mint')) return '#2fd17e';
  if (normName.includes('lemon')) return '#f1c40f';
  if (normName.includes('rose')) return '#e84393';
  if (normName.includes('cinnamon')) return '#d35400';
  if (normName.includes('blueberry')) return '#3498db';
  if (normName.includes('vanilla')) return '#f5cd79';
  if (normName.includes('chai') || normName.includes('latte')) return '#ffeaa7';
  if (normName.includes('pear')) return '#a8e6cf';
  if (normName.includes('lime')) return '#badc58';
  if (normName.includes('grey') || normName.includes('tea')) return '#706fd3';
  if (normName.includes('red') || normName.includes('straw')) return '#ff7979';
  if (normName.includes('water')) return '#ff5252';
  if (normName.includes('carda')) return '#b33939';

  const presets = [
    '#6B4EFF',
    '#C9A66B',
    '#5BA18B',
    '#D46A6A',
    '#fd79a8',
    '#e17055',
  ];
  const hash = name
    .split('')
    .reduce((acc, char) => acc + char.charCodeAt(0), 0);
  return presets[hash % presets.length];
}

export interface UserProfile {
  username: string;
  email: string;
  avatarUrl: string;
  isLoggedIn: boolean;
}

export const AUTH_KEY = 'hf_auth_user';

export function getAuthUser(): UserProfile {
  const data = localStorage.getItem(AUTH_KEY);
  if (!data) {
    const defaultUser: UserProfile = {
      username: 'no name',
      email: 'user@example.com',
      avatarUrl: '',
      isLoggedIn: false,
    };
    localStorage.setItem(AUTH_KEY, JSON.stringify(defaultUser));
    return defaultUser;
  }
  try {
    return JSON.parse(data);
  } catch (e) {
    return {
      username: 'no name',
      email: 'user@example.com',
      avatarUrl: '',
      isLoggedIn: false,
    };
  }
}

export function saveAuthUser(user: UserProfile) {
  localStorage.setItem(AUTH_KEY, JSON.stringify(user));
  window.dispatchEvent(new Event('hf-auth-change'));
}
